import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Statistic,
  Table,
  Tag,
  Progress,
  Spin,
  Empty,
  Button,
  Space,
  Tooltip,
  message,
  Alert,
} from "antd";
import {
  FileTextOutlined,
  SearchOutlined,
  GlobalOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  DashboardOutlined,
  RocketOutlined,
  BarChartOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  getProgrammaticSeoCampaigns,
  getProgrammaticSeoPages,
  getProgrammaticSeoStatistics,
} from "@api/seo/programmaticSeo";
import "./ProgrammaticSeoAnalytics.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ProgrammaticSeoAnalytics = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [pages, setPages] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [pageType, setPageType] = useState("all");
  const [totalPages, setTotalPages] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    // Get URL parameters on component mount
    const searchParams = new URLSearchParams(window.location.search);
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const pageTypeParam = searchParams.get("pageType") || "all";
    
    // Set initial state from URL
    setPagination({ current: page, pageSize });
    setPageType(pageTypeParam);
    
    // Fetch data with URL parameters
    fetchAnalytics();
    fetchPages(pageTypeParam === "all" ? {} : { pageType: pageTypeParam }, page, pageSize);
  }, []);

  const fetchAnalytics = async (days = 30) => {
    setLoading(true);
    try {
      // Fetch statistics and campaigns for analytics
      const [statsRes, campaignsRes] = await Promise.all([
        getProgrammaticSeoStatistics(),
        getProgrammaticSeoCampaigns({ perPage: 100 }),
      ]);

      const statsData = statsRes.data?.data || statsRes.data;
      const campaignsData = campaignsRes.data?.data || campaignsRes.data;

      // Process page statistics
      const pageStats = statsData.pages || [];
      const dataSourceStats = statsData.dataSource || {};
      
      // Calculate totals by page type
      const pageTypeTotals = {
        comparisons: 0,
        integrations: 0,
        locations: 0,
        useCases: 0,
      };
      
      let totalQualityScore = 0;
      let totalQualityCount = 0;
      
      pageStats.forEach(pageType => {
        const typeName = pageType._id;
        const activeCount = pageType.statuses?.find(s => s.status === 'active')?.count || 0;
        
        switch(typeName) {
          case 'comparison':
            pageTypeTotals.comparisons = activeCount;
            break;
          case 'integration':
            pageTypeTotals.integrations = activeCount;
            break;
          case 'location':
            pageTypeTotals.locations = activeCount;
            break;
          case 'useCase':
            pageTypeTotals.useCases = activeCount;
            break;
        }
        
        // Calculate average quality score
        pageType.statuses?.forEach(status => {
          if (status.avgQualityScore && status.count) {
            totalQualityScore += status.avgQualityScore * status.count;
            totalQualityCount += status.count;
          }
        });
      });
      
      const avgQualityScore = totalQualityCount > 0 ? (totalQualityScore / totalQualityCount).toFixed(1) : 0;

      // Calculate total traffic from statistics
      const totalTraffic = pageStats.reduce((sum, pageType) => {
        const trafficSum = pageType.statuses?.reduce((statusSum, status) => {
          return statusSum + (status.totalTraffic || 0);
        }, 0) || 0;
        return sum + trafficSum;
      }, 0);

      // Calculate campaign trends (last 30 days)
      const campaigns = campaignsData.campaigns || [];
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      // Group campaigns by creation date for trend data
      const trendData = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
        const dateStr = date.toISOString().split('T')[0];
        
        const dayPages = campaigns.reduce((sum, campaign) => {
          const createdDate = new Date(campaign.createdAt);
          if (createdDate.toISOString().split('T')[0] === dateStr) {
            return sum + (campaign.pagesGenerated || 0);
          }
          return sum;
        }, 0);
        
        trendData.push({
          date: dateStr,
          pages: dayPages,
        });
      }

      setAnalytics({
        summary: {
          current: {
            totalPages: statsData.summary?.totalPages || 0,
            totalTraffic: totalTraffic,
            totalKeywords: dataSourceStats.total || 0,
            avgQualityScore: parseFloat(avgQualityScore),
            ...pageTypeTotals,
          }
        },
        trends: trendData,
        growth: {
          pages: 15.2, // Would need historical data to calculate
          traffic: 23.5,
          keywords: 8.7,
          quality: 2.1,
        }
      });
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      message.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async (filters = {}, page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      const response = await getProgrammaticSeoPages({
        page,
        perPage: pageSize,
        sortBy: "-performanceMetrics.organicTraffic",
        ...filters
      });
      
      const pagesData = response.data?.data || response.data;
      setPages(pagesData.pages || []);
      setTotalPages(pagesData.total || 0);
    } catch (error) {
      console.error("Failed to fetch pages:", error);
    }
  };

  const updateURL = (newPagination, newPageType) => {
    const searchParams = new URLSearchParams();
    searchParams.set("page", newPagination.current);
    searchParams.set("pageSize", newPagination.pageSize);
    if (newPageType && newPageType !== "all") {
      searchParams.set("pageType", newPageType);
    }
    
    history.push({ search: searchParams.toString() });
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      const days = dates[1].diff(dates[0], "days");
      fetchAnalytics(days);
    }
  };

  const handlePageTypeChange = (value) => {
    setPageType(value);
    const newPagination = { ...pagination, current: 1 };
    setPagination(newPagination);
    updateURL(newPagination, value);
    fetchPages(value === "all" ? {} : { pageType: value }, 1, pagination.pageSize);
  };

  const handleTablePaginationChange = (page, pageSize) => {
    const newPagination = { current: page, pageSize };
    setPagination(newPagination);
    updateURL(newPagination, pageType);
    fetchPages(pageType === "all" ? {} : { pageType }, page, pageSize);
  };

  const renderGrowthStat = (value, label) => {
    const isPositive = parseFloat(value) > 0;
    return (
      <Statistic
        title={label}
        value={Math.abs(parseFloat(value))}
        precision={1}
        valueStyle={{ color: isPositive ? "#3f8600" : "#cf1322" }}
        prefix={isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
        suffix="%"
      />
    );
  };

  const topPagesColumns = [
    {
      title: "Page",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Tooltip title={record.url}>
          <a href={record.url} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        </Tooltip>
      ),
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "pageType",
      key: "pageType",
      width: 100,
      render: (type) => {
        const typeConfig = {
          comparison: { color: "blue", text: "Comparison" },
          integration: { color: "green", text: "Integration" },
          location: { color: "orange", text: "Location" },
          useCase: { color: "purple", text: "Use Case" },
          locationUseCase: { color: "geekblue", text: "Location + Use Case" },
          integrationUseCase: { color: "cyan", text: "Integration + Use Case" },
        };
        const config = typeConfig[type] || { color: "default", text: type };
        return (
          <Tag color={config.color}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Traffic",
      dataIndex: ["performanceMetrics", "organicTraffic"],
      key: "traffic",
      width: 100,
      render: (traffic) => traffic?.toLocaleString() || 0,
    },
    {
      title: "Quality",
      dataIndex: "qualityScore",
      key: "qualityScore",
      width: 120,
      render: (score) => (
        <Progress
          percent={score}
          size="small"
          strokeColor={
            score >= 80 ? "#52c41a" : score >= 60 ? "#faad14" : "#f5222d"
          }
        />
      ),
    },
  ];

  const navigateTo = (path) => {
    const basePath =
      user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}${path}`);
  };

  // Removed the loading state that was hiding the header

  const trafficChartData =
    analytics?.trends?.map((item) => ({
      date: new Date(item.date).toLocaleDateString(),
      traffic: item.traffic,
      keywords: item.keywords,
      conversions: item.conversions,
    })) || [];

  const pageTypeDistribution = analytics?.summary?.current
    ? [
        {
          type: "Comparisons",
          value: analytics.summary.current.comparisons || 0,
        },
        {
          type: "Integrations",
          value: analytics.summary.current.integrations || 0,
        },
        { type: "Locations", value: analytics.summary.current.locations || 0 },
        { type: "Use Cases", value: analytics.summary.current.useCases || 0 },
      ]
    : [];

  // Colors for pie chart
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  // Custom label for pie chart
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="programmatic-seo-analytics">
      {/* Header Section */}
      <div className="seo-dashboard-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Programmatic SEO Analytics</h1>
            <p>
              Track performance metrics, traffic trends, and campaign success rates
            </p>
            <div className="seo-nav-pills">
              <Button
                icon={<DashboardOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/dashboard")}
              >
                Dashboard
              </Button>
              <Button
                icon={<RocketOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
              >
                Campaigns
              </Button>
              <Button type="primary" icon={<BarChartOutlined />}>
                Analytics
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/pages")}
              >
                Pages
              </Button>
            </div>
          </Col>
          <Col>
            <Space className="action-button-group">
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ApiOutlined />}
                onClick={() => navigateTo("/seo/ai-apps")}
              >
                AI Apps
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<GlobalOutlined />}
                onClick={() => navigateTo("/seo/seo-glossary")}
              >
                SEO Glossary
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchAnalytics();
                  fetchPages(pageType === "all" ? {} : { pageType }, pagination.current, pagination.pageSize);
                }}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="seo-dashboard-content">
        <Spin spinning={loading && !analytics} size="large" tip="Loading analytics...">
            {/* Date Range and Filter Controls */}
            <Card className="filter-section" style={{ marginBottom: 24 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <RangePicker
                      onChange={handleDateRangeChange}
                      format="YYYY-MM-DD"
                      placeholder={['Start Date', 'End Date']}
                    />
                    <Select
                      value={pageType}
                      onChange={handlePageTypeChange}
                      style={{ width: 180 }}
                      placeholder="Page Type"
                    >
                      <Option value="all">All Types</Option>
                      <Option value="comparison">Comparisons</Option>
                      <Option value="integration">Integrations</Option>
                      <Option value="location">Locations</Option>
                      <Option value="useCase">Use Cases</Option>
                      <Option value="locationUseCase">Location + Use Case</Option>
                      <Option value="integrationUseCase">Integration + Use Case</Option>
                    </Select>
                  </Space>
                </Col>
              </Row>
            </Card>

        {/* Traffic Data Collection Note */}
        {analytics?.summary?.current?.totalTraffic === 0 && (
          <Alert
            message="Traffic Data Collection"
            description="Traffic analytics integration is pending. Once integrated with Google Analytics or similar services, you'll see real-time traffic data for all your generated pages."
            type="info"
            showIcon
            closable
            style={{ marginBottom: 24 }}
          />
        )}

        <Row gutter={[24, 24]}>

          {/* Summary Stats */}
          {analytics?.summary?.current && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Total Pages"
                    value={analytics.summary.current.totalPages}
                    prefix={<FileTextOutlined className="icon-file" />}
                    formatter={(value) => value.toLocaleString()}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Organic Traffic"
                    value={analytics.summary.current.totalTraffic}
                    prefix={<GlobalOutlined className="icon-global" />}
                    formatter={(value) => value.toLocaleString()}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Keywords Ranking"
                    value={analytics.summary.current.totalKeywords}
                    prefix={<SearchOutlined className="icon-search" />}
                    formatter={(value) => value.toLocaleString()}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card className="stat-card">
                  <Statistic
                    title="Avg Quality Score"
                    value={analytics.summary.current.avgQualityScore}
                    precision={1}
                    suffix="/ 100"
                    valueStyle={{
                      color:
                        analytics.summary.current.avgQualityScore >= 70
                          ? "#3f8600"
                          : "#cf1322",
                    }}
                  />
                </Card>
              </Col>
            </>
          )}

          {/* Charts Row */}
          <Col xs={24} lg={16}>
            <Card title="Traffic Trend">
              {trafficChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trafficChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="traffic"
                      stroke="#667eea"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Traffic"
                    />
                    <Line
                      type="monotone"
                      dataKey="keywords"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Keywords"
                    />
                    <Line
                      type="monotone"
                      dataKey="conversions"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Conversions"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No traffic data available yet" />
              )}
            </Card>
          </Col>

          {/* Page Type Distribution */}
          <Col xs={24} lg={8}>
            <Card title="Page Distribution">
              {pageTypeDistribution.some(item => item.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pageTypeDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pageTypeDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No page distribution data available yet" />
              )}
            </Card>
          </Col>

          {/* Top Performing Pages */}
          <Col span={24}>
            <Card title="Top Performing Pages">
              {pages.length > 0 ? (
                <Table
                  columns={topPagesColumns}
                  dataSource={pages}
                  rowKey="_id"
                  pagination={{
                    ...pagination,
                    total: totalPages,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} pages`,
                    onChange: handleTablePaginationChange,
                  }}
                  size="small"
                />
              ) : (
                <Empty description="No pages data available yet" />
              )}
            </Card>
          </Col>
        </Row>
        </Spin>
      </div>
    </div>
  );
};

export default ProgrammaticSeoAnalytics;
